import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useMainStore } from '@/modules/shared/stores/main.store'
import type { Entity } from '@/modules/shared/types/entity.type'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import { useAuthStore } from '@/modules/shared/stores/auth.store'

import { useCustomerSearchData } from '../data/customer-search.data'
import {
  useCustomerFormStore,
  useCustomerFormStoreAction,
} from '../stores/customer-form.store'
import { useClearCustomerStore } from './use-clear-store.hook'
import { useStartOrderByCustomer } from './use-start-order-by-customer.hook'
import { type EntitySearchPayload, entitySearchService } from '@/modules/shared/services/entity-search.service'


export const useCustomerSearch = () => {
  const navigate = useNavigate()
  const { versionUrl } = useAuthStore()
  const isApiV3 = versionUrl.includes('/v3')

  // Estados para paginação infinita
  const [isLoadingSearch, setIsLoadingSearch] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const currentSearchRef = useRef<EntitySearchPayload | null>(null)
  const initialSearchStartedRef = useRef(false)

  // Estado para o dialog de catálogo
  const [catalogoDialogOpen, setCatalogoDialogOpen] = useState(false)

  const { deleteCustomer } = useCustomerSearchData()
  const startOrderOfClientHook = useStartOrderByCustomer()
  const clearStoreHook = useClearCustomerStore()

  const {
    customers,
    actions: {
      setCustomers,
      setConfirmation,
      setCloseConfirmation,
      setIsLoading,
    },
  } = useMainStore()

  const { clientSelected } = useCustomerFormStore()
  const { setOperationType, setCustomerSelected } = useCustomerFormStoreAction

  // Reset da busca
  const resetSearch = useCallback(() => {
    setCustomers(undefined)
    setIsLoadingSearch(false)
    setIsLoadingMore(false)
    setHasMore(false)
    setTotalRecords(0)
    setCurrentPage(1)
    currentSearchRef.current = null
  }, [setCustomers])

  // Função de busca melhorada com paginação
  const handleSearchClient = useCallback(async ({ payload }: PayloadSearch) => {
    const searchPayload: EntitySearchPayload = {
      entityType: '1',
      filter: payload.filter,
      inputValue: payload.filter === 'cnpjcpf' || payload.filter === 'id_entidade'
        ? payload.inputValue.toString().replace(/\D/g, '')
        : payload.inputValue,
      ...(isApiV3 && { dataInclusao: new Date().toISOString() })
    }

    if (searchPayload.inputValue?.length <= 0 || searchPayload.inputValue === undefined) {
       searchPayload.inputValue = '%20'
    }

    console.log('🔍 Iniciando busca de clientes:', searchPayload)

    setIsLoadingSearch(true)
    setCustomers([])
    setCurrentPage(1)
    setHasMore(false)
    setTotalRecords(0)
    currentSearchRef.current = searchPayload

    try {
      const result = await entitySearchService.searchInitialEntities(searchPayload)
      if (currentSearchRef.current !== searchPayload) return

      console.log('📊 Resultado da busca:', result)

      setCustomers(result.entities)
      setTotalRecords(result.totalRecords)
      setHasMore(result.hasMore)
      setCurrentPage(2)
    } catch (error) {
      if (currentSearchRef.current !== searchPayload) return
      console.error('❌ Erro na busca:', error)
      toast.error('Erro ao buscar clientes. Tente novamente.')
      setCustomers([])
      setHasMore(false)
      setTotalRecords(0)
    } finally {
      if (currentSearchRef.current === searchPayload) setIsLoadingSearch(false)
    }
  }, [isApiV3, setCustomers])

  useEffect(() => {
    if (initialSearchStartedRef.current) return
    initialSearchStartedRef.current = true
    handleSearchClient({ payload: { filter: 'razao_social_nome', inputValue: '' } })
  }, [handleSearchClient])

  // Função para carregar mais clientes
  const loadMoreCustomers = useCallback(async () => {
    if (!currentSearchRef.current || isLoadingMore || !hasMore) return

    console.log('📥 Carregando mais clientes...')

    setIsLoadingMore(true)

    try {
      const result = await entitySearchService.loadMoreEntities(
        currentSearchRef.current,
        currentPage,
        customers?.length || 0,
        totalRecords
      )

      if (result.entities.length > 0) {
        const currentCustomers = customers || []
        setCustomers([...currentCustomers, ...result.entities])
        setCurrentPage(prev => prev + 1)
      }

      setHasMore(result.hasMore)

      console.log('✅ Mais clientes carregados:', {
        novos: result.entities.length,
        total: (customers?.length || 0) + result.entities.length,
        hasMore: result.hasMore
      })
    } catch (error) {
      console.error('❌ Erro ao carregar mais:', error)
      toast.error('Erro ao carregar mais clientes')
    } finally {
      setIsLoadingMore(false)
    }
  }, [currentPage, hasMore, isLoadingMore, customers, totalRecords, setCustomers])

  // Funções originais mantidas
  const handleBackPage = () => {
    navigate('/')
    resetSearch()
  }

  const handleSelectClientToStartActions = (client: Entity | null) => {
    setCustomerSelected(client)
  }

  const handleStartAddClient = () => {
    setOperationType('add')
    navigate('/cliente/add')
  }

  const handleStartEditClient = () => {
    setOperationType('edit')
    navigate(`/cliente/edit/${clientSelected?.idEntidade}`)
  }

  const handleDeleteCustomer = () => {
    setConfirmation({
      isOpen: true,
      message: 'Deseja excluir este cliente?',
      onClose: () => {
        setCloseConfirmation()
      },
      onConfirm: () => {
        const { idEntidade } = clientSelected

        deleteCustomer({
          payload: {
            clientId: idEntidade.toString(),
          },
        })
        setCloseConfirmation()
      },
      title: 'Confirmação de exclusão',
    })
  }

  const handleStartViewClient = (clientId?: string) => {
    setOperationType('view')

    if (clientSelected?.idEntidade) {
      navigate(`/cliente/view/${clientSelected?.idEntidade}`)
      return
    }

    const client = customers?.find(
      (c) => c.idEntidade === Number.parseInt(clientId),
    )

    setCustomerSelected(client)
    navigate(`/cliente/view/${clientId}`)
  }

  const handleStartOrder = () => {
    startOrderOfClientHook.action.handleStartOrder()
  }

  const handleNavigationClientPerfil = () => {
    navigate(`/cliente/profile/${clientSelected?.idEntidade}`)
  }

  const makeCall = () => {
    if (clientSelected?.fone) {
      const cleanPhone = clientSelected.fone.replace(/\D/g, '')
      if (cleanPhone) {
        window.open(`tel:${cleanPhone}`, '_self')
      } else {
        toast.info('Número de telefone não disponível ou inválido.')
      }
    } else {
      toast.info('Número de telefone não disponível para este cliente.')
    }
  }

  const sendEmail = () => {
    if (clientSelected?.email) {
      const subject = encodeURIComponent(`Contato - ${clientSelected.razaoSocialNome}`)
      const body = encodeURIComponent(`Olá ${clientSelected.razaoSocialNome},\n\n`)
      window.open(`mailto:${clientSelected.email}?subject=${subject}&body=${body}`, '_self')
    } else {
      toast.info('Email não disponível para este cliente.')
    }
  }

  const handleOpenCatalogoDialog = () => {
    setCatalogoDialogOpen(true)
  }

  const handleCloseCatalogoDialog = () => {
    setCatalogoDialogOpen(false)
  }

  const selectFunctionToActions = (typeAction: string) => {
    const actions = {
      makeCall: makeCall,
      sendEmail: sendEmail,
      delete: handleDeleteCustomer,
      startOrder: handleStartOrder,
      startEdit: handleStartEditClient,
      startView: handleStartViewClient,
      perfil: handleNavigationClientPerfil,
      catalogoLink: handleOpenCatalogoDialog,
    }

    const action = actions[typeAction]
    action()
  }

  // Effects originais
  useEffect(() => {
    clearStoreHook.action.clearAllStore()
  }, [])

  useEffect(() => {
    if (isLoadingSearch) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [isLoadingSearch, setIsLoading])

  return {
    // Estados originais
    customers,
    clientSelected,
    isDisabledEdit: !clientSelected?.permiteAlteracao,
    isDisabledDelete: !clientSelected?.permiteExclusao,

    // Funções originais
    handleBackPage,
    handleStartAddClient,
    handleSearchClient,
    selectFunctionToActions,
    handleSelectClientToStartActions,
    handleStartViewClient,

    // Novos estados e funções para scroll infinito
    isLoading: isLoadingSearch,
    isLoadingMore,
    hasMore,
    totalRecords,
    loadMoreCustomers,
    resetSearch,

    // Estados e funções do catálogo
    catalogoDialogOpen,
    handleCloseCatalogoDialog,
  }
}